import { zodResolver } from '@hookform/resolvers/zod';
import { useNetwork } from '@react-hooks-library/core';
import { Add } from 'iconsax-react';
import { isEmpty, pick } from 'lodash-es';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';
import type { EditorActions } from '@/components/conversations/conversation-form/quill-editor';
import { MentionMenuContainer } from '@/components/conversations/mention-menu/mention-menu';
import SymbolMenu, { type SymbolMenuActions } from '@/components/symbol/symbol-menu';
import { ConversationFormProvider } from '@/hooks/conversations/use-conversation-form';
import useConversationStore, { useConversationHandlers } from '@/hooks/conversations/use-conversation-store';
import { useGroupMentionSources } from '@/hooks/groups/use-group-members';
import { useIsDesktop } from '@/hooks/use-user-platform';
import {
  ConversationMessageContentType,
  type ConversationMessageTypeUnion,
  conversationMessageUnionScheme,
} from '@/models/conversations/conversation';
import { type Uploadable, uploadableScheme } from '@/models/uploadable';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useEmbedLayout } from '@/stores/device';
import { addTranslateToast } from '@/stores/toast';
import { EmbedLayout } from '@/utils/embed-layout';
import { FILE_LIMIT_MAP, FileType, getFileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';
import { convertFileToUploadable } from '@/utils/uploadable';
import { ConversationAttachmentForm } from '../attachment-form/conversation-attachment-form';
import ConversationRecordForm from '../conversation-record-form';
import ConversationFormButton from './conversation-form-button';
import ConversationFormQuoteArea from './conversation-form-quote-area';
import ConversationTextArea from './conversation-text-area';

export interface ConversationInputProps {
  isLoading?: boolean;
  disabled?: boolean;
}

export interface ConversationInputForwardedProps {
  attach: (files: File[]) => void;
  quote: (quote: ConversationMessageTypeUnion) => void;
  mention: (mention: ConversationMessageTypeUnion) => void;
}

export const ConversationForm = forwardRef<ConversationInputForwardedProps, ConversationInputProps>(
  ({ disabled, isLoading }, forwardedRef) => {
    const isDesktop = useIsDesktop();
    const editorRef = useRef<EditorActions>(null);
    const symbolMenuRef = useRef<SymbolMenuActions>(null);

    const [isAttachmentOpen, toggleAttachmentOpen, setAttachmentOpen] = useToggle(false);
    const [isSymbolMenuOpen, _, setSymbolMenuOpen] = useToggle(false);
    const [isRecorderOpen, toggleRecorderOpen, setRecorderOpen] = useToggle(false);

    const { type: sessionType, id: sessionId } = useConversationStore();
    const sessionKey = generateSessionKey(sessionType, sessionId);

    const { handleSubmit } = useConversationHandlers();
    const layout = useEmbedLayout();
    const { isOnline } = useNetwork();
    const { updateDraft, deleteDraft, hydrated } = useConversationSessionStore();
    const draft = useConversationSessionStore((s) => s.drafts[sessionKey]);
    const { sources: mentionSources } = useGroupMentionSources(sessionId);

    const memoDraft = useMemo(() => (hydrated ? draft : undefined), [hydrated, draft]);

    const handleEmoji = (emoji: string) => {
      editorRef.current?.insertText(emoji, undefined, false);
    };

    const handleSticker = async (sticker: string) => {
      const quote = form.getValues('Quote');
      await handleSubmit({
        Content: sticker,
        Quote: quote,
        ContentType: ConversationMessageContentType.Emoticon,
      });
      handleResetForm();
      if (isDesktop) {
        setSymbolMenuOpen(false);
      }
    };

    const form = useForm<ConversationFormDataType>({
      reValidateMode: 'onSubmit',
      resolver: zodResolver(conversationFormSchema),
      defaultValues: {
        Content: '',
      },
    });

    function handleUpdateDraft() {
      const formValues = form.getValues();

      const draft = pick(formValues, ['Content', 'Quote', 'AtIDs']);

      if (draft.Content || draft.Quote) {
        updateDraft(sessionKey, draft);
      } else {
        deleteDraft(sessionKey);
      }
    }

    function handleMentionMember(memberId: number) {
      editorRef.current?.insertMention(memberId);

      const atIds = memberId === -1 ? [0] : [memberId];

      form.setValue('AtIDs', atIds);
    }

    function handleResetForm() {
      form.reset();
      deleteDraft(sessionKey);
    }

    function handleToggleFileInput() {
      toggleAttachmentOpen();
      setRecorderOpen(false);
    }

    async function submit(data: ConversationFormDataType) {
      const { Attachment, AttachmentType, Content, ...rest } = data;

      const contentDom = new DOMParser().parseFromString(Content ?? '', 'text/html');
      const stringContent = contentDom.body.textContent ?? '';
      const atIds = Array.from(contentDom.body.querySelectorAll('span[data-at-id]'))
        .map((span) => Number(span.getAttribute('data-at-id') ?? '0'))
        .filter((id) => !Number.isNaN(id) && mentionSources.some((m) => m.id === id));

      if (!isOnline) {
        addTranslateToast({
          type: 'warning',
          message: 'connection.disconnected',
        });
        return;
      }

      setSymbolMenuOpen(false);
      editorRef.current?.reset(!Attachment || isEmpty(Attachment));
      handleResetForm();
      setAttachmentOpen(false);

      if (!isEmpty(stringContent)) {
        await handleSubmit({
          ...rest,
          Content: stringContent ?? '',
          AtIDs: atIds,
        });
      }

      if (Attachment && !isEmpty(Attachment)) {
        await handleSubmit({
          ...rest,
          AtIDs: atIds,
          Attachment,
          AttachmentType,
        });
      }
    }

    useImperativeHandle(forwardedRef, () => ({
      quote: (quote) => {
        if (!disabled) {
          form.setValue('Quote', quote);
          handleUpdateDraft();
        }
      },
      mention: (mention) => {
        if (!disabled && 'GroupID' in mention) {
          handleMentionMember(mention.UserID);
        }
      },
      attach: (files) => {
        if (!disabled) {
          let attachment: Uploadable[] = form.getValues('Attachment') ?? [];
          let attachmentType = form.getValues('AttachmentType');

          for (const file of files) {
            const uploadable = convertFileToUploadable(file);
            const fileType = getFileType(file);
            if (!attachmentType || fileType !== attachmentType) {
              attachmentType = fileType;
            }

            if (fileType === FileType.Image) {
              attachment.push(uploadable);
            } else {
              attachment = [uploadable];
              break;
            }
          }

          form.setValue('AttachmentType', attachmentType);
          form.setValue('Attachment', attachment);
          if (attachmentType === FileType.File) {
            form.handleSubmit(submit)();
          }
        }
      },
    }));

    useEffect(() => {
      if (draft) {
        form.setValue('Content', draft.Content, { shouldDirty: true });
        form.setValue('AtIDs', draft.AtIDs);
        form.setValue('Quote', draft.Quote);
      } else if (hydrated) {
        handleResetForm();
      }
    }, [draft, form.setValue, hydrated]);

    return (
      <form
        onSubmit={form.handleSubmit(submit)}
        className='flex flex-1 flex-col items-stretch h-full gap-2 desktop:relative desktop:bg-base-100 desktop:gap-0'>
        <ConversationFormProvider
          value={{
            form,
            canRecord: layout === EmbedLayout.FULL,
            submit: form.handleSubmit(submit),
            handleUpdateDraft,
            isRecorderOpen,
            toggleRecorderOpen,
            mentionSources,
          }}>
          <ConversationFormQuoteArea />

          <div className='relative flex min-h-14 items-center gap-2 px-5  desktop:gap-6 desktop:pb-2 desktop:bg-base-100'>
            {layout === EmbedLayout.FULL && (
              <button
                type='button'
                className={cn('relative btn btn-square btn-ghost btn-sm desktop:self-center', {
                  hidden: isRecorderOpen,
                })}
                disabled={disabled}
                onClick={handleToggleFileInput}>
                {isAttachmentOpen && <span className='absolute inset-0 z-10 hidden desktop:block' />}
                <Add
                  className={cn('size-6 transition-transform', {
                    'rotate-45': isAttachmentOpen,
                  })}
                />
              </button>
            )}

            {sessionType === 'group' && mentionSources.length > 0 && (
              <MentionMenuContainer onClose={() => editorRef.current?.closeMentionMenu()} />
            )}

            <div className='flex items-center gap-2 flex-1 desktop:min-h-0 desktop:gap-4 transition-colors desktop:textarea desktop:bg-neutral-0  desktop:border-2 desktop:border-secondary desktop:py-1.5 desktop:rounded-lg'>
              {isRecorderOpen ? (
                <ConversationRecordForm isLoading={isLoading} />
              ) : hydrated ? (
                <ConversationTextArea
                  key={sessionId}
                  ref={editorRef}
                  disabled={disabled}
                  isLoading={isLoading}
                  initialValue={memoDraft?.Content}
                  isSymbolMenuOpen={isSymbolMenuOpen}
                  onToggleSymbolMenu={(open) => {
                    const setTo = open ?? !isSymbolMenuOpen;
                    setSymbolMenuOpen(setTo);
                    if (setTo) {
                      setAttachmentOpen(false);
                      setRecorderOpen(false);
                      editorRef.current?.blur();
                    } else {
                      editorRef.current?.focus();
                    }
                  }}
                />
              ) : null}

              <ConversationFormButton
                isLoading={isLoading}
                disabled={disabled}
              />
            </div>
          </div>

          {layout === EmbedLayout.FULL && (
            <>
              <ConversationAttachmentForm
                open={isAttachmentOpen}
                isLoading={isLoading}
                disabled={disabled}
                onClose={() => setAttachmentOpen(false)}
              />
              <SymbolMenu
                ref={symbolMenuRef}
                isOpen={isSymbolMenuOpen}
                disabled={disabled}
                onClose={() => setSymbolMenuOpen(false)}
                onEmoji={handleEmoji}
                onSticker={handleSticker}
              />
            </>
          )}
        </ConversationFormProvider>
      </form>
    );
  },
);

ConversationForm.displayName = 'ConversationForm';

const conversationFormSchema = z
  .union([
    z.object({
      Content: z.string().optional(),
      Quote: conversationMessageUnionScheme.optional(),
      Attachment: z.array(uploadableScheme).optional(),
      AttachmentType: z.nativeEnum(FileType),
      AtIDs: z.array(z.number()).optional(),
    }),
    z.object({
      Content: z.string().trim().min(1, 'errors.string.length|count=1'),
      Quote: conversationMessageUnionScheme.optional(),
      ContentType: z.nativeEnum(ConversationMessageContentType).default(ConversationMessageContentType.Text).optional(),
      Attachment: z.never().optional(),
      AttachmentType: z.null().optional(),
      AtIDs: z.array(z.number()).optional(),
    }),
  ])
  .refine(
    (data) => {
      if (data.Attachment && data.AttachmentType) {
        return data.Attachment.length <= FILE_LIMIT_MAP[data.AttachmentType as FileType];
      }
      return true;
    },
    { message: 'error.multi-file-over-limit', path: ['Attachment'] },
  );

export type ConversationFormDataType = z.infer<typeof conversationFormSchema>;
